import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Button,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { BiSearch, BiX } from 'react-icons/bi';
import { toast } from 'react-toastify';
import { useGetCurrentUserCustomerIdsQuery } from '../queries/getCurrentUserCustomerIds.query.generated';
import { type GetUserDetailsQuery } from '../queries/getUserDetails.query.generated';
import { useStore } from '@store';
import { canManageUserAccess } from '../utils/roleUtils';
import { useMarketAccess } from '../hooks/useMarketAccess';
import { useRoleManagement } from '../hooks/useRoleManagement';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { resolveRooftopChange, type RooftopChangeResult } from '../utils/rooftopChangeResolver';
import type { Tier } from '@utils/tiers';
import { getUserTierInfo } from '@utils/tiers';
import RooftopChangeModal from './RooftopChangeModal';
import TierChip from '@features/rooftops/components/TierChip';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';

// -- Types --

type UserDetail = NonNullable<GetUserDetailsQuery['users']>[0];

interface UserCustomersTabProps {
  user: UserDetail;
  refetch: () => void;
}

interface CustomerRow {
  id: number;
  name: string;
  tier: string | null;
}

type SortKey = 'id' | 'name' | 'tier' | 'access';
type SortOrder = 'asc' | 'desc';

const ACCESS_OPTIONS = [
  { label: 'Has Access', value: 'linked' },
  { label: 'No Access', value: 'unlinked' },
];

const TIER_OPTIONS = [
  { label: 'Standard', value: 'standard' },
  { label: 'Lite', value: 'lite' },
];

const StyledSelect = styled(Select)(({ theme }) => ({
  minWidth: 150,
  height: 36,
  fontSize: 13,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
}));

// -- Component --

const UserCustomersTab: React.FC<UserCustomersTabProps> = ({ user, refetch }) => {
  const currentUserId = useStore(state => state.user?.profile?.id);
  const currentUserRole = useStore(state => state.user?.profile?.default_role);
  const { grantMarket, revokeMarket } = useMarketAccess();
  const { syncPermissions, assignRole, revokeDriverAccess } = useRoleManagement();
  const { grantFeatures } = useFeatureAccess();

  // Modal state for confirming significant rooftop changes
  const [pendingChange, setPendingChange] = useState<{
    customerId: number;
    checked: boolean;
    rooftopName: string;
    rooftopTier: Tier;
    result: RooftopChangeResult;
  } | null>(null);
  const [applyingChange, setApplyingChange] = useState(false);
  useReloadGuard(applyingChange, 'Rooftop access change');

  // Fetch the current (logged-in) user's linked rooftops — the set they may grant from.
  const { data: currentUserCustomerData } = useGetCurrentUserCustomerIdsQuery({
    variables: { userId: currentUserId || 0 },
    skip: !currentUserId,
    fetchPolicy: 'cache-and-network',
  });

  // -- Server-linked customer IDs for the VIEWED user --
  const serverCustomerIds = useMemo(
    () => new Set(user.users_usertocustomers.map(c => c.customer_id)),
    [user],
  );

  // -- Optimistic state --
  const [optimisticAdds, setOptimisticAdds] = useState<Set<number>>(new Set());
  const [optimisticRemoves, setOptimisticRemoves] = useState<Set<number>>(new Set());

  useEffect(() => {
    setOptimisticAdds(prev => {
      const next = new Set<number>();
      for (const id of prev) {
        if (!serverCustomerIds.has(id)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
    setOptimisticRemoves(prev => {
      const next = new Set<number>();
      for (const id of prev) {
        if (serverCustomerIds.has(id)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [serverCustomerIds]);

  const linkedIds = useMemo(() => {
    const ids = new Set(serverCustomerIds);
    for (const id of optimisticAdds) ids.add(id);
    for (const id of optimisticRemoves) ids.delete(id);
    return ids;
  }, [serverCustomerIds, optimisticAdds, optimisticRemoves]);

  // -- Build rows from the current user's linked rooftops --
  const allCustomers: CustomerRow[] = useMemo(() => {
    const rows: CustomerRow[] = (currentUserCustomerData?.usertocustomers ?? [])
      .map((utc: any) => ({
        id: utc.customer?.id,
        name: utc.customer?.name ?? `Customer #${utc.customer_id}`,
        tier: utc.customer?.tier ?? null,
      }))
      .filter((r: CustomerRow) => r.id != null);

    // Always include any of the viewed user's linked rooftops not already in the list
    // (so toggles still show even if the current user lost access)
    const baseIds = new Set(rows.map(r => r.id));
    for (const utc of user.users_usertocustomers) {
      if (!baseIds.has(utc.customer.id)) {
        rows.push({
          id: utc.customer.id,
          name: utc.customer.name ?? `Customer #${utc.customer.id}`,
          tier: (utc.customer as any).tier ?? null,
        });
      }
    }

    return rows;
  }, [currentUserCustomerData, user.users_usertocustomers]);

  // -- Self-removal guard --
  const isSelf = user.id === currentUserId;

  // Only a super admin may modify a super admin's rooftop access.
  const canManage = canManageUserAccess(currentUserRole, user.default_role);

  // -- Apply a rooftop change (simple or after modal confirmation) --
  const applyRooftopChange = useCallback(
    async (customerId: number, checked: boolean, roleOverride?: string, featureIds?: Set<number>) => {
      if (checked) {
        setOptimisticAdds(prev => new Set(prev).add(customerId));
        setOptimisticRemoves(prev => { const next = new Set(prev); next.delete(customerId); return next; });
      } else {
        setOptimisticRemoves(prev => new Set(prev).add(customerId));
        setOptimisticAdds(prev => { const next = new Set(prev); next.delete(customerId); return next; });
      }

      try {
        if (checked) {
          await grantMarket(user.id, customerId);
        } else {
          await revokeMarket(user.id, customerId);
        }

        // Apply role override if the resolver determined one
        if (roleOverride) {
          await assignRole(user.id, roleOverride);
        } else {
          const newCount = checked ? serverCustomerIds.size + 1 : serverCustomerIds.size - 1;
          await syncPermissions(user.id, user.default_role, newCount);
        }

        // Grant features if the modal provided them
        if (featureIds && featureIds.size > 0) {
          await grantFeatures(user.id, Array.from(featureIds));
        }

        // Revoke driver access if resolver flagged it
        const resolveResult = pendingChange?.result;
        if (resolveResult?.changes.some(c => c.revokesDriverAccess)) {
          await revokeDriverAccess(user.id);
        }

        refetch();
      } catch (err: any) {
        if (checked) {
          setOptimisticAdds(prev => { const next = new Set(prev); next.delete(customerId); return next; });
        } else {
          setOptimisticRemoves(prev => { const next = new Set(prev); next.delete(customerId); return next; });
        }
        toast.error(`Failed to update rooftop access: ${err.message}`);
      }
    },
    [user.id, user.default_role, serverCustomerIds.size, grantMarket, revokeMarket, assignRole, syncPermissions, grantFeatures, revokeDriverAccess, refetch, pendingChange],
  );

  // -- Toggle handler (entry point) --
  const handleToggle = useCallback(
    (customerId: number, checked: boolean) => {
      // Only a super admin may modify a super admin's access (defense-in-depth).
      if (!canManage) return;

      if (!checked && isSelf) {
        toast.warning('You cannot remove your own rooftop access');
        return;
      }

      // Find the rooftop tier
      const customer = allCustomers.find(c => c.id === customerId);
      // Cross-rooftop `customers.tier` read (named exception): the product-access snapshot covers only the working rooftop, and this decision spans every rooftop the user is linked to.
      const rooftopTier = (customer?.tier || 'standard') as Tier;

      // Run the resolver
      const result = resolveRooftopChange({
        action: checked ? 'add' : 'remove',
        rooftopTier,
        customerId,
        user: user as any,
      });

      if (result.isSimple) {
        // No significant transition — apply directly
        applyRooftopChange(customerId, checked);
      } else {
        // Significant transition — open confirmation modal
        setPendingChange({
          customerId,
          checked,
          rooftopName: customer?.name || 'Unknown',
          rooftopTier,
          result,
        });
      }
    },
    [user, allCustomers, isSelf, canManage, applyRooftopChange],
  );

  const handleModalConfirm = useCallback(
    async (selectedFeatureIds?: Set<number>) => {
      if (!pendingChange) return;
      setApplyingChange(true);

      // Determine role override from resolver changes
      const roleChange = pendingChange.result.changes.find(c => c.type === 'role-change');
      const roleOverride = roleChange?.toRole;

      await applyRooftopChange(pendingChange.customerId, pendingChange.checked, roleOverride, selectedFeatureIds);

      setApplyingChange(false);
      setPendingChange(null);
    },
    [pendingChange, applyRooftopChange],
  );

  const targetUserTiers = useMemo(() => {
    const info = getUserTierInfo(user.users_usertocustomers);
    return info.tiers;
  }, [user.users_usertocustomers]);

  // -- Table state --
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState<SortKey>('name');
  const [order, setOrder] = useState<SortOrder>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [tierFilter, setTierFilter] = useState<string | null>(null);
  const [accessFilter, setAccessFilter] = useState<string | null>(null);

  const hasActiveFilters = !!tierFilter || !!accessFilter;

  const clearAllFilters = () => {
    setTierFilter(null);
    setAccessFilter(null);
    setPage(0);
  };

  const handleSort = (property: SortKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  // -- Filter, sort, paginate --
  const filteredCustomers = useMemo(() => {
    let rows = [...allCustomers];

    if (tierFilter) {
      rows = rows.filter(c => c.tier === tierFilter);
    }
    if (accessFilter === 'linked') {
      rows = rows.filter(c => linkedIds.has(c.id));
    } else if (accessFilter === 'unlinked') {
      rows = rows.filter(c => !linkedIds.has(c.id));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(c => c.name.toLowerCase().includes(q));
    }

    rows.sort((a, b) => {
      let aVal: any;
      let bVal: any;
      if (orderBy === 'access') {
        aVal = linkedIds.has(a.id) ? 1 : 0;
        bVal = linkedIds.has(b.id) ? 1 : 0;
      } else {
        aVal = (a as any)[orderBy] ?? '';
        bVal = (b as any)[orderBy] ?? '';
      }
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return rows;
  }, [allCustomers, search, orderBy, order, tierFilter, accessFilter, linkedIds]);

  const paginatedCustomers = useMemo(
    () => filteredCustomers.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredCustomers, page, rowsPerPage],
  );

  // -- Empty state --
  if (allCustomers.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
          No rooftops available
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
          Your account does not have access to any rooftops to assign.
        </Typography>
      </Box>
    );
  }

  return (
  <>
    <Paper sx={{ p: 0, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{
        px: 3, py: 2,
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        borderBottom: '1px solid', borderColor: 'divider',
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, whiteSpace: 'nowrap' }}>
          Rooftop Access
        </Typography>
        <Box flex={1} />
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {linkedIds.size} of {allCustomers.length} linked
        </Typography>
      </Box>

      {/* Search */}
      <Box sx={{ px: 3, pt: 1.5 }}>
        <TextField
          size="small"
          placeholder="Search by name..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <BiSearch size={18} color="#778088" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, height: 36 } }}
        />
      </Box>

      {/* Filters */}
      <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <FormControl size="small">
          <StyledSelect
            value={tierFilter ?? ''}
            onChange={e => { setTierFilter(e.target.value === '' ? null : e.target.value as string); setPage(0); }}
            displayEmpty
            renderValue={(value): React.ReactNode => {
              if (!value) return <span style={{ color: '#9ca3af' }}>Tier</span>;
              const opt = TIER_OPTIONS.find(o => o.value === value);
              return (opt?.label ?? value) as React.ReactNode;
            }}
          >
            <MenuItem value="">All Tiers</MenuItem>
            {TIER_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13, textTransform: 'capitalize' }}>
                {opt.label}
              </MenuItem>
            ))}
          </StyledSelect>
        </FormControl>

        <FormControl size="small">
          <StyledSelect
            value={accessFilter ?? ''}
            onChange={e => { setAccessFilter(e.target.value === '' ? null : e.target.value as string); setPage(0); }}
            displayEmpty
            renderValue={(value): React.ReactNode => {
              if (!value) return <span style={{ color: '#9ca3af' }}>Access</span>;
              const opt = ACCESS_OPTIONS.find(o => o.value === value);
              return (opt?.label ?? value) as React.ReactNode;
            }}
          >
            <MenuItem value="">All</MenuItem>
            {ACCESS_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
                {opt.label}
              </MenuItem>
            ))}
          </StyledSelect>
        </FormControl>

        {hasActiveFilters && (
          <Button
            size="small"
            startIcon={<BiX />}
            onClick={clearAllFilters}
            sx={{ fontSize: 12, textTransform: 'none' }}
          >
            Clear All
          </Button>
        )}
      </Box>

      {/* Table */}
      <Box sx={{ px: 3, pb: 2, pt: 1 }}>
        {filteredCustomers.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No rooftops match the current filters.</Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
              Showing {paginatedCustomers.length} of {filteredCustomers.length}
            </Typography>

            <TableContainer>
              <Table size="small" sx={{ minWidth: 400 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 160 }}>
                      <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: 100 }}>
                      <TableSortLabel active={orderBy === 'tier'} direction={orderBy === 'tier' ? order : 'asc'} onClick={() => handleSort('tier')}>
                        Tier
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: 80 }}>
                      <TableSortLabel active={orderBy === 'access'} direction={orderBy === 'access' ? order : 'asc'} onClick={() => handleSort('access')}>
                        Access
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCustomers.map((customer) => {
                    const hasAccess = linkedIds.has(customer.id);
                    return (
                      <TableRow
                        key={customer.id}
                        sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                            {customer.name}
                          </Typography>
                        </TableCell>
                        <TableCell><TierChip tier={customer.tier} /></TableCell>
                        <TableCell>
                          {isSelf && hasAccess ? (
                            <Tooltip title="You cannot remove your own rooftop access">
                              <span>
                                <Switch size="small" checked disabled />
                              </span>
                            </Tooltip>
                          ) : !canManage ? (
                            <Tooltip title="Only a super admin can change a super admin's access">
                              <span>
                                <Switch size="small" checked={hasAccess} disabled />
                              </span>
                            </Tooltip>
                          ) : (
                            <Switch
                              size="small"
                              checked={hasAccess}
                              onChange={(_e, checked) => handleToggle(customer.id, checked)}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredCustomers.length > 10 && (
              <TablePagination
                component="div"
                count={filteredCustomers.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            )}
          </>
        )}
      </Box>
    </Paper>

    {/* Confirmation modal for significant rooftop changes */}
    <RooftopChangeModal
      open={!!pendingChange}
      onClose={() => setPendingChange(null)}
      onConfirm={handleModalConfirm}
      changes={pendingChange?.result.changes ?? []}
      rooftopName={pendingChange?.rooftopName}
      userName={user.display_name}
      action={pendingChange?.checked ? 'add' : 'remove'}
      loading={applyingChange}
      targetUserTiers={targetUserTiers}
    />
  </>
  );
};

export default UserCustomersTab;
